import { formatMoney } from '@theme/money-formatting';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

class UheatProductAddOns extends HTMLElement {
  connectedCallback() {
    this.#mainPrice = Number(this.dataset.mainPrice) || 0;
    this.#section = this.closest('.shopify-section');

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('keydown', this.#handleKeydown);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section?.addEventListener('quantity-selector:update', this.#handleQuantityChange);
    this.#section?.addEventListener('uheat:bulk-order-update', this.#handleBulkOrderUpdate);

    if (this.#section) {
      this.#observer = new MutationObserver(() => {
        const priceChanged = this.#syncMainPriceFromPicker();
        if (priceChanged) this.#updatePurchaseSummary();
        this.#syncAcceleratedCheckout();
        this.#syncAddToCartButton();
      });
      this.#observer.observe(this.#section, { childList: true, subtree: true });
    }

    requestAnimationFrame(() => {
      this.#syncMainPriceFromPicker();
      this.#updateSelection();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section?.removeEventListener('quantity-selector:update', this.#handleQuantityChange);
    this.#section?.removeEventListener('uheat:bulk-order-update', this.#handleBulkOrderUpdate);
    this.#observer?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {Element | null} */
  #section = null;

  /** @type {number} */
  #mainPrice = 0;

  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-add-on-multi-quantity]')) {
      this.#normaliseMultiVariantQuantity(event.target);
      this.#handleMultiVariantQuantityUpdate(event.target);
      return;
    }

    if (event.target instanceof HTMLInputElement && event.target.matches('[data-add-on-quantity]')) {
      this.#normaliseAddOnQuantity(event.target);
      this.#handleAddOnQuantityUpdate();
      return;
    }

    if (event.target instanceof HTMLSelectElement && event.target.matches('[data-add-on-variant-select]')) {
      this.#applyVariantSelection(event.target);
      return;
    }

    if (event.target instanceof HTMLInputElement && event.target.matches('input[data-add-on-choice]')) {
      this.#updateSelection();
    }
  };

  #handlePointerDown = (event) => {
    const select = event.target instanceof Element
      ? event.target.closest('[data-add-on-variant-select]')
      : null;

    if (select instanceof HTMLSelectElement) this.#applyVariantSelection(select);
  };

  /** @param {HTMLSelectElement} select */
  #applyVariantSelection(select) {
    const option = select.selectedOptions[0];
    const card = select.closest('[data-add-on-card]');
    const choice = card?.querySelector('input[data-add-on-variant]');

    if (!(option instanceof HTMLOptionElement) || !(choice instanceof HTMLInputElement)) return;

    const available = option.dataset.addOnAvailable === 'true';
    const priceCents = Number(option.dataset.addOnPriceCents) || 0;
    const selectionName = option.dataset.addOnName || '';

    choice.value = option.value;
    choice.dataset.addOnPriceCents = String(priceCents);
    choice.dataset.addOnName = selectionName;
    choice.disabled = !available;
    card?.classList.toggle('is-unavailable', !available);

    const price = card?.querySelector('[data-add-on-price]');
    if (price) price.textContent = available ? this.#formatMoney(priceCents) : 'Sold out';

    const image = card?.querySelector('.uheat-add-ons__image');
    if (image instanceof HTMLImageElement && option.dataset.imageSrc) {
      image.src = option.dataset.imageSrc;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.alt = option.dataset.imageAlt || selectionName;
    }

    const dialogPrice = card?.querySelector('[data-add-on-dialog-price]');
    if (dialogPrice) dialogPrice.textContent = this.#formatMoney(priceCents);

    if (!available) return;

    choice.checked = true;
    choice.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;

    const multiQuantityButton = target?.closest('[data-add-on-multi-action]');
    if (multiQuantityButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#adjustMultiVariantQuantity(
        multiQuantityButton,
        multiQuantityButton.dataset.addOnMultiAction === 'increase' ? 1 : -1
      );
      return;
    }

    const quantityButton = target?.closest('[data-add-on-quantity-action]');
    if (quantityButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#adjustAddOnQuantity(quantityButton.dataset.addOnQuantityAction === 'increase' ? 1 : -1);
      return;
    }

    const thumbnail = target?.closest('[data-add-on-gallery-thumbnail]');
    if (thumbnail instanceof HTMLButtonElement) {
      const dialog = thumbnail.closest('[data-add-on-gallery]');
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        this.#showGalleryImage(dialog, Number(thumbnail.dataset.galleryIndex) || 0);
      }
      return;
    }

    const navigation = target?.closest('[data-add-on-gallery-nav]');
    if (navigation instanceof HTMLButtonElement) {
      const dialog = navigation.closest('[data-add-on-gallery]');
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        this.#stepGallery(dialog, Number(navigation.dataset.galleryDirection) || 1);
      }
      return;
    }

    if (target instanceof HTMLDialogElement && target.matches('.uheat-add-ons__dialog')) {
      const bounds = target.getBoundingClientRect();
      const clickedOutside =
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom;

      if (clickedOutside) target.close();
      return;
    }

    const previewButton = target?.closest('[data-add-on-preview]');
    if (!previewButton) return;

    event.preventDefault();
    event.stopPropagation();

    const dialogId = previewButton.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    if (dialog instanceof HTMLDialogElement) {
      this.#prepareGallery(dialog);
      dialog.showModal();
    }
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const dialog = event.target instanceof Element ? event.target.closest('[data-add-on-gallery]') : null;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    event.preventDefault();
    this.#stepGallery(dialog, event.key === 'ArrowLeft' ? -1 : 1);
  };

  /** @param {HTMLDialogElement} dialog */
  #prepareGallery(dialog) {
    const choiceId = dialog.dataset.addOnChoiceId;
    const choice = choiceId ? document.getElementById(choiceId) : null;
    const selectedBadge = dialog.querySelector('[data-add-on-gallery-selection]');

    if (selectedBadge instanceof HTMLElement) {
      selectedBadge.hidden = !(choice instanceof HTMLInputElement && choice.checked);
    }

    this.#showGalleryImage(dialog, Number(dialog.dataset.galleryIndex) || 0);
  }

  /** @param {HTMLDialogElement} dialog @param {number} direction */
  #stepGallery(dialog, direction) {
    const currentIndex = Number(dialog.dataset.galleryIndex) || 0;
    this.#showGalleryImage(dialog, currentIndex + direction);
  }

  /** @param {HTMLDialogElement} dialog @param {number} requestedIndex */
  #showGalleryImage(dialog, requestedIndex) {
    const thumbnails = Array.from(dialog.querySelectorAll('[data-add-on-gallery-thumbnail]'));
    if (!thumbnails.length) return;

    const index = ((requestedIndex % thumbnails.length) + thumbnails.length) % thumbnails.length;
    const activeThumbnail = thumbnails[index];
    const mainImage = dialog.querySelector('.uheat-add-ons__dialog-image');

    if (activeThumbnail instanceof HTMLElement && mainImage instanceof HTMLImageElement) {
      const nextSource = activeThumbnail.dataset.imageSrc;
      if (nextSource) mainImage.src = nextSource;
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.alt = activeThumbnail.dataset.imageAlt || '';
    }

    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const isCurrent = thumbnailIndex === index;
      thumbnail.classList.toggle('is-current', isCurrent);
      thumbnail.setAttribute('aria-pressed', String(isCurrent));
    });

    dialog.dataset.galleryIndex = String(index);
    const count = dialog.querySelector('[data-add-on-gallery-count]');
    if (count) count.textContent = `Image ${index + 1} of ${thumbnails.length}`;
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const price = Number(detail?.resource?.price);
        if (Number.isFinite(price)) this.#mainPrice = price;
        this.#syncMainPriceFromPicker();
        this.#updatePurchaseSummary();
        this.#syncAddToCartButton();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[uheat-product-addons] Variant update failed:', error);
      });
  };

  #handleQuantityChange = () => {
    this.#updatePurchaseSummary();
    this.#syncAddToCartButton();
  };

  #handleBulkOrderUpdate = () => {
    this.#updatePurchaseSummary();
  };

  #getSelectedChoice() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-add-on-choice]:checked'));
  }

  #syncMainPriceFromPicker() {
    const picker =
      this.#section?.querySelector('variant-picker[data-template-product-match="true"]') ||
      this.#section?.querySelector('variant-picker');
    if (!(picker instanceof HTMLElement)) return false;

    const scripts = Array.from(picker.querySelectorAll('script[type="application/json"]')).reverse();

    for (const script of scripts) {
      try {
        const variant = JSON.parse(script.textContent || '');
        const price = Number(variant?.price);
        if (!Array.isArray(variant) && Number.isFinite(price)) {
          if (price === this.#mainPrice) return false;
          this.#mainPrice = price;
          return true;
        }
      } catch {
        continue;
      }
    }

    return false;
  }

  #updateSelection() {
    const selected = this.#getSelectedChoice();
    const selectedNone = Boolean(selected && !selected.matches('[data-add-on-variant], [data-add-on-multi-choice]'));
    if (selectedNone) this.#resetMultiVariantQuantities();

    const selectedItems = this.getSelectedItems();
    const hasAddOn = selectedItems.length > 0;
    const cards = this.querySelectorAll('[data-add-on-card]');

    for (const card of cards) {
      const input = card.querySelector('input[data-add-on-choice]');
      card.classList.toggle('is-selected', input === selected && (!input.matches('[data-add-on-multi-choice]') || hasAddOn));
    }

    this.dataset.selectedAddOnQuantity = String(
      selectedItems.reduce((total, item) => total + item.quantity, 0)
    );

    this.#updatePurchaseSummary();
    this.#syncAcceleratedCheckout();
    this.#syncAddToCartButton();
  }

  #updatePurchaseSummary() {
    const selected = this.#getSelectedChoice();
    const selectedItems = this.getSelectedItems();
    const hasAddOn = selectedItems.length > 0;
    const confirmation = this.querySelector('[data-add-on-confirmation]');
    const name = this.querySelector('[data-add-on-summary]');
    const quantity = this.#getQuantity();
    const addOnTotal = selectedItems.reduce((total, item) => total + item.price * item.quantity, 0);
    const bulkOrder = this.#getBulkOrder();
    const mainTotal = bulkOrder ? Number(bulkOrder.dataset.selectedTotal) || 0 : this.#mainPrice * quantity;
    const total = mainTotal + addOnTotal;

    if (confirmation instanceof HTMLElement) confirmation.hidden = !hasAddOn;
    if (name && hasAddOn) {
      const itemCount = selectedItems.reduce((total, item) => total + item.quantity, 0);
      name.textContent = selected?.matches('[data-add-on-multi-choice]')
        ? selectedItems.length === 1
          ? `${selectedItems[0].name} × ${selectedItems[0].quantity}`
          : `${selectedItems.length} options (${itemCount} items)`
        : selectedItems[0].name;
    }

    const totalElement = this.querySelector('[data-add-on-total]');
    if (totalElement && hasAddOn) {
      totalElement.textContent =
        bulkOrder && quantity === 0
          ? 'Choose quantities to see total'
          : `${this.dataset.totalLabel || 'Order total'}: ${this.#formatMoney(total)}`;
    }
  }

  #syncAcceleratedCheckout() {
    const hasSelectedAddOns = this.getSelectedItems().length > 0;
    const acceleratedCheckout = this.#section?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (!(acceleratedCheckout instanceof HTMLElement)) return;

    if (hasSelectedAddOns) {
      acceleratedCheckout.hidden = true;
      acceleratedCheckout.dataset.uheatAddOnHidden = 'true';
      return;
    }

    if (acceleratedCheckout.dataset.uheatAddOnHidden === 'true') {
      acceleratedCheckout.hidden = false;
      delete acceleratedCheckout.dataset.uheatAddOnHidden;
    }
  }

  #syncAddToCartButton() {
    const selectedItems = this.getSelectedItems();
    const hasAddOn = selectedItems.length > 0;
    const addOnTotal = selectedItems.reduce((total, item) => total + item.price * item.quantity, 0);
    const buttons = this.#section?.querySelectorAll('[ref="addToCartButton"]') || [];

    for (const button of buttons) {
      const text = button.querySelector('.add-to-cart-text__content > span > span');
      if (!(button instanceof HTMLButtonElement) || !text) continue;

      if (!button.dataset.uheatDefaultText) {
        button.dataset.uheatDefaultText = text.textContent?.trim() || 'Add to cart';
      }

      const nextText = hasAddOn
        ? `${this.dataset.buttonLabel || 'Add selected products'} — ${this.#formatMoney(
            this.#mainPrice * this.#getQuantity() + addOnTotal
          )}`
        : button.dataset.uheatDefaultText;

      if (text.textContent !== nextText) text.textContent = nextText;
    }
  }

  #getQuantity() {
    const bulkOrder = this.#getBulkOrder();
    if (bulkOrder) return Number(bulkOrder.dataset.selectedQuantity) || 0;

    const input = this.#section?.querySelector('product-form-component input[name="quantity"]');
    return input instanceof HTMLInputElement ? Number(input.value) || 1 : 1;
  }

  #getBulkOrder() {
    return this.#section?.querySelector('uheat-product-bulk-order') || null;
  }

  getSelectedItems() {
    const selected = this.#getSelectedChoice();

    if (selected?.matches('[data-add-on-multi-choice]')) {
      const card = selected.closest('[data-add-on-card]');
      return Array.from(card?.querySelectorAll('[data-add-on-multi-quantity]') || [])
        .filter((input) => input instanceof HTMLInputElement && !input.disabled && Number(input.value) > 0)
        .map((input) => ({
          variantId: input.dataset.addOnVariantId || '',
          quantity: Math.max(0, Number.parseInt(input.value || '0', 10) || 0),
          price: Number(input.dataset.addOnPriceCents) || 0,
          name: input.dataset.addOnName || '',
        }))
        .filter((item) => item.variantId && item.quantity > 0);
    }

    if (!selected?.matches('[data-add-on-variant]') || !selected.value) return [];

    return [{
      variantId: selected.value,
      quantity: this.getSelectedQuantity(),
      price: Number(selected.dataset.addOnPriceCents) || 0,
      name: selected.dataset.addOnName || '',
    }];
  }

  getSelectedQuantity() {
    if (this.dataset.addOnQuantityEnabled !== 'true') return 1;

    const input = this.querySelector('[data-add-on-quantity]');
    return input instanceof HTMLInputElement ? Math.max(1, Number.parseInt(input.value || '1', 10) || 1) : 1;
  }

  #adjustAddOnQuantity(direction) {
    const input = this.querySelector('[data-add-on-quantity]');
    if (!(input instanceof HTMLInputElement)) return;

    const current = this.#normaliseAddOnQuantity(input);
    input.value = String(Math.max(1, current + direction));
    this.#handleAddOnQuantityUpdate();
  }

  #adjustMultiVariantQuantity(button, direction) {
    const row = button.closest('.uheat-add-ons__multi-row');
    const input = row?.querySelector('[data-add-on-multi-quantity]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    const current = this.#normaliseMultiVariantQuantity(input);
    input.value = String(Math.max(0, current + direction));
    this.#handleMultiVariantQuantityUpdate(input);
  }

  #normaliseMultiVariantQuantity(input) {
    const value = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
    input.value = String(value);
    return value;
  }

  #handleMultiVariantQuantityUpdate(input) {
    const card = input.closest('[data-add-on-card]');
    const choice = card?.querySelector('[data-add-on-multi-choice]');
    const hasQuantities = Array.from(card?.querySelectorAll('[data-add-on-multi-quantity]') || [])
      .some((quantityInput) => quantityInput instanceof HTMLInputElement && Number(quantityInput.value) > 0);

    if (choice instanceof HTMLInputElement && hasQuantities) {
      choice.checked = true;
    } else if (!hasQuantities) {
      const noneChoice = this.querySelector('.uheat-add-ons__none input[data-add-on-choice]');
      if (noneChoice instanceof HTMLInputElement) noneChoice.checked = true;
    }

    this.#updateSelection();
    const quantity = this.getSelectedItems().reduce((total, item) => total + item.quantity, 0);
    this.dispatchEvent(new CustomEvent('uheat:add-on-quantity-update', {
      bubbles: true,
      detail: { quantity },
    }));
  }

  #resetMultiVariantQuantities() {
    for (const input of this.querySelectorAll('[data-add-on-multi-quantity]')) {
      if (input instanceof HTMLInputElement) input.value = '0';
    }
  }

  #normaliseAddOnQuantity(input) {
    const value = Math.max(1, Number.parseInt(input.value || '1', 10) || 1);
    input.value = String(value);
    return value;
  }

  #handleAddOnQuantityUpdate() {
    const quantity = this.getSelectedQuantity();
    this.dataset.selectedAddOnQuantity = String(quantity);
    this.#updatePurchaseSummary();
    this.#syncAddToCartButton();
    this.dispatchEvent(new CustomEvent('uheat:add-on-quantity-update', {
      bubbles: true,
      detail: { quantity },
    }));
  }

  /** @param {number} value */
  #formatMoney(value) {
    return formatMoney(value, this.dataset.moneyFormat || '£{{amount}}', this.dataset.currency || 'GBP');
  }
}

if (!customElements.get('uheat-product-addons')) {
  customElements.define('uheat-product-addons', UheatProductAddOns);
}
